import { AlertTriangleIcon, BuildingIcon, MapPinIcon, PackageIcon, Receipt, TruckIcon, UserIcon, Undo2Icon } from "lucide-react";
import { useMemo } from "react";
import { type VariableDefinition, WORKFLOW_VARIABLES } from "@babackapp/variables-registry";
import { useWorkflowsEditorConfig } from "../context/workflows-editor-config-context";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";


/**
 * Extended variable type for UI presentation in workflows editor
 * Adds icon for visual representation
 */
export type EditorVariableUI = VariableDefinition & {
  icon: React.ComponentType<{ className?: string }>;
};

/**
 * Maps variable groups to their corresponding icons
 */
const GROUP_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  shop: BuildingIcon,
  parcel: PackageIcon,
  order: Receipt,
  customer: UserIcon,
  carrier: TruckIcon,
  address: MapPinIcon,
  pickupPoint: MapPinIcon,
  claim: AlertTriangleIcon,
  return: Undo2Icon,
};


/**
 * Tracking implementation of the domain variables: the tracking registry
 * with the shop name as example value. Referenced by the tracking config
 * (tracking-provider.tsx). Memoized so consumers keyed on the array
 * identity (unavailable-variables memo) only recompute when it changes.
 */
export function useTrackingEditorVariables(): VariableDefinition[] {
  const { data: shop } = useCurrentShopSuspense();

  return useMemo(
    () =>
      WORKFLOW_VARIABLES.map((variable) => {
        if (variable.id === "shop.name") {
          return { ...variable, exampleValue: shop.name };
        }
        return variable;
      }),
    [shop.name],
  );
}


/**
 * Returns the current domain's workflow variables (from the host-provided
 * editor config).
 */
export function useDefaultEditorVariables(): VariableDefinition[] {
  const { useVariables } = useWorkflowsEditorConfig();
  return useVariables();
}


/**
 * Returns workflow variables with UI metadata for display
 */
export function useEditorVariablesWithUI(): EditorVariableUI[] {
  const variables = useDefaultEditorVariables();

  return variables.map((variable) => ({
    ...variable,
    icon: GROUP_ICONS[variable.group] || PackageIcon,
  }));
}


/**
 * Returns variables grouped by their group property
 */
export function useDefaultEditorVariablesGroupped() {
  return useDefaultEditorVariables().reduce(
    (acc, variable) => {
      if (!acc[variable.group]) {
        acc[variable.group] = [];
      }
      acc[variable.group].push(variable);
      return acc;
    },
    {} as Record<string, VariableDefinition[]>,
  );
}


/**
 * Returns variables entries for iteration
 */
export function useEditorVariablesEntries() {
  return Object.entries(useDefaultEditorVariablesGroupped());
}
